import { TGetIndustryListResDto } from "@/api/services/global/industry/dtos/get-industry-list.dto";
import { useMe } from "@/api/services/main/auth";
import { CACHED_TIME } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { TOption } from "@/types/common";
import { globalApi } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export const fetchIndustryListApi = async (): Promise<TGetIndustryListResDto> => {
  const { data } = await globalApi.get<TGetIndustryListResDto>("/industries");
  return data;
};

export const useGetIndustryList = () => {
  const { data, isLoading, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.GLOBAL.INDUSTRY_LIST],
    queryFn: () => fetchIndustryListApi(),
    staleTime: CACHED_TIME,
    gcTime: CACHED_TIME,
  });

  // hiddenIndustryIds はログインユーザーの所属 (RSO / 企業 / 支援ユーザーは作成元RSO) を BE が
  // 解決して get-me が返す。company/RSO はそれぞれ独立した hiddenIndustryIds を持つ。
  const { data: meResult } = useMe();
  const hiddenIndustryIds = useMemo(
    () => new Set((meResult?.data?.hiddenIndustryIds ?? []).map(String)),
    [meResult],
  );

  const allIndustries = useMemo(() => data?.data ?? [], [data]);

  // Keep only published industries. Tolerant to `isPublished` being absent in the response
  // (treated as published) so the list isn't emptied if BE already filters server-side.
  const publishedIndustries = useMemo(
    () => allIndustries.filter((item) => item.isPublished !== false),
    [allIndustries],
  );

  /** Industries excluding those hidden by the user's RSO/company. */
  const visibleIndustries = useMemo(
    () => publishedIndustries.filter((item) => !hiddenIndustryIds.has(String(item.id))),
    [publishedIndustries, hiddenIndustryIds],
  );

  const industryOptions: TOption[] = useMemo(
    () => visibleIndustries.map((item) => ({ label: item.name, value: item.id })),
    [visibleIndustries],
  );

  return { data: publishedIndustries, allIndustries, visibleIndustries, industryOptions, isLoading, ...rest };
};
